import React from 'react';
import classnames from 'classnames';
import MediaBreakpoints from 'Utils/MediaBreakpoints';
import RentalItem from './RentalItem';
import RentalBundle from './RentalBundle';
import RentalStaff from './RentalStaff';
import { connect } from 'react-redux';
import CurrencyLabel from 'Utils/CurrencyLabel';
import { shouldHidePrice } from 'HelperFunctions/HidePricing';
import { DISCOUNT_DISPLAY_MODES } from 'Constants/discountDisplay';

class RentalSection extends React.Component {
  calculateSubTotal = () => {
    const { section } = this.props;
    const { sectionMemberships } = section;
    return sectionMemberships
      .filter(
        (sectionMembership) =>
          sectionMembership.sectionMembershipType !== 'blank' &&
          sectionMembership._destroy !== '1' &&
          sectionMembership.rentalInventory
      )
      .reduce((sum, sectionMembership) => {
        if (sectionMembership.parentConnectionType === 'duplicate') return sum;
        const inv = sectionMembership.rentalInventory;
        // The Subtotal always reflects the discounted (real) value, matching
        // the per-row Subtotal cells and the admin tables.
        if (inv.productType === 'staffs') return sum + (parseFloat(inv.cost) || 0);
        if (inv.productType === 'bundles') {
          return (
            sum +
            (parseFloat(inv.finalValue) ||
              parseFloat(inv.discountedPrice) ||
              parseFloat(inv.selectedPrice) ||
              0)
          );
        }
        return sum + (parseFloat(inv.selectedPrice) || 0);
      }, 0);
  };
  render() {
    const {
      rental,
      section,
      approvalStatus,
      showItemImages,
      showItemNotes,
      showUnitPricingToCustomers,
      showSubtotalsInSections,
      tileView,
      storefrontShopSetting,
      discountDisplay,
    } = this.props;

    const { sectionMemberships } = section;
    const hideSubtotalPricing = shouldHidePrice(
      rental.approvalStatus,
      storefrontShopSetting
    );

    // Item-level fee/discount columns (only items carry totalFees/totalDiscounts).
    // Fees column shows whenever any item has fees; Discounts column only in
    // 'column' mode, matching the admin tables.
    const sectionItems = sectionMemberships
      .filter(
        (sm) =>
          sm.rentalInventory &&
          sm._destroy !== '1' &&
          sm.parentConnectionType !== 'duplicate'
      )
      .map((sm) => sm.rentalInventory);
    const hasItemFees = sectionItems.some(
      (inv) => (parseFloat(inv.totalFees) || 0) > 0
    );
    const hasItemDiscounts = sectionItems.some((inv) => {
      // Bundles carry selectedPrice (pre-discount) and discountedPrice (post),
      // not selectedPriceBeforeDiscount/totalDiscounts like items.
      if (inv.productType === 'bundles') {
        const pre = parseFloat(inv.selectedPrice);
        const post = parseFloat(inv.discountedPrice);
        return !isNaN(pre) && !isNaN(post) && post < pre;
      }
      const pre = parseFloat(inv.selectedPriceBeforeDiscount);
      const sel = parseFloat(inv.selectedPrice);
      return (
        (parseFloat(inv.totalDiscounts) || 0) > 0 ||
        (!isNaN(pre) && !isNaN(sel) && pre !== sel)
      );
    });
    const showFeesCol = !hideSubtotalPricing && hasItemFees;
    const showDiscountsCol =
      !hideSubtotalPricing &&
      discountDisplay === DISCOUNT_DISPLAY_MODES.COLUMN &&
      hasItemDiscounts;

    return (
      <div>
        <h4>{section.name}</h4>
        <table
          className={classnames({
            grid: true,
            thumbnail: tileView,
          })}
        >
          <MediaBreakpoints
            desktop={
              <thead>
                <tr>
                  <th colSpan={tileView && showItemImages ? '2' : '1'}>Item</th>
                  <th className='sm right'>Quantity</th>
                  {approvalStatus === 'approved' &&
                    showUnitPricingToCustomers &&
                    storefrontShopSetting.shopShowItemPricing && (
                      <th className='medSm right'>Rate</th>
                    )}
                  {showFeesCol && <th className='medSm right'>Fees</th>}
                  {showDiscountsCol && (
                    <th className='medSm right'>Discounts</th>
                  )}
                  <th className='medSm right'>Subtotal</th>
                </tr>
              </thead>
            }
            mobile={
              <thead>
                {/* Rows render as labeled lines on mobile, so the header is just
                    the section name (no per-column headers). */}
                <tr>
                  <th className='title'>{section.name}</th>
                </tr>
              </thead>
            }
          />
          {sectionMemberships.map((sectionMembership, index) => {
            const item = sectionMembership.rentalInventory;
            if (!item) return;
            const inventoryType = item.productType;
            const { parentConnectionType } = sectionMembership;

            if (inventoryType === 'items' || inventoryType === 'addons') {
              return (
                <RentalItem
                  key={index}
                  approvalStatus={approvalStatus}
                  showItemImages={showItemImages}
                  showItemNotes={showItemNotes}
                  sectionMembership={sectionMembership}
                  showUnitPricingToCustomers={showUnitPricingToCustomers}
                  parentConnectionType={parentConnectionType}
                  rentalItem={item}
                  tileView={tileView}
                  rental={rental}
                  discountDisplay={discountDisplay}
                  showFeesCol={showFeesCol}
                  showDiscountsCol={showDiscountsCol}
                />
              );
            } else if (inventoryType === 'bundles') {
              return (
                <RentalBundle
                  approvalStatus={approvalStatus}
                  showItemImages={showItemImages}
                  showItemNotes={showItemNotes}
                  showUnitPricingToCustomers={showUnitPricingToCustomers}
                  rentalBundle={item}
                  key={index}
                  tileView={tileView}
                  rental={rental}
                  discountDisplay={discountDisplay}
                  showFeesCol={showFeesCol}
                  showDiscountsCol={showDiscountsCol}
                />
              );
            } else if (inventoryType === 'staffs') {
              return (
                <RentalStaff
                  key={index}
                  showItemImages={showItemImages}
                  showItemNotes={showItemNotes}
                  showUnitPricingToCustomers={showUnitPricingToCustomers}
                  item={item}
                  tileView={tileView}
                  rental={rental}
                  showFeesCol={showFeesCol}
                  showDiscountsCol={showDiscountsCol}
                />
              );
            } else {
              return null;
            }
          })}
        </table>
        {!hideSubtotalPricing && showSubtotalsInSections && (
          <div className='subtotal'>
            <strong>Subtotal</strong>
            <CurrencyLabel value={this.calculateSubTotal()} />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { parsedStorefrontSettings, location } = state.locations;
  const storefrontShopSetting = parsedStorefrontSettings.storefrontShopSetting;

  return { storefrontShopSetting, location };
};
export default connect(mapStateToProps)(RentalSection);
